// Page pour rejoindre une équipe via lien d'invitation
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Users, Check, Loader2, AlertCircle, Gift } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RejoindreEquipePage() {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [teamInfo, setTeamInfo] = useState(null);

  const token = localStorage.getItem('token');
  const isLoggedIn = !!token;

  const joinTeam = async () => {
    if (!isLoggedIn) {
      // Sauvegarder le code d'invitation et rediriger vers l'inscription
      localStorage.setItem('pendingTeamInvite', inviteCode);
      toast.info('Connecte-toi pour rejoindre l\'équipe');
      navigate('/reseau/connexion');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_URL}/api/pro/teams/join/${inviteCode}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await res.json();

      if (res.ok) {
        setSuccess(true);
        setTeamInfo(data);
        toast.success('Bienvenue dans l\'équipe !');
        
        // Rediriger vers le cockpit pro après 2 secondes
        setTimeout(() => {
          navigate('/cockpit-pro/collaboration/equipe');
        }, 2000);
      } else {
        setError(data.detail || 'Erreur lors de la jonction');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  // Vérifier au chargement si on a un code d'invitation en attente après connexion
  useEffect(() => {
    const pendingInvite = localStorage.getItem('pendingTeamInvite');
    if (pendingInvite && isLoggedIn && pendingInvite === inviteCode) {
      localStorage.removeItem('pendingTeamInvite');
      joinTeam();
    }
  }, [isLoggedIn]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1E3A5F] to-[#2d5a8a] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
        {success ? (
          // Succès
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-[#1E3A5F] mb-2">
              Bienvenue dans l'équipe !
            </h1>
            <p className="text-gray-600 mb-4">
              Tu as rejoint <strong>{teamInfo?.team_name}</strong>
            </p>
            <div className="bg-[#E8650A]/10 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-center gap-2 text-[#E8650A] font-semibold mb-1">
                <Gift size={20} />
                1 mois gratuit !
              </div>
              <p className="text-sm text-gray-600">
                Ton essai gratuit se termine le{' '}
                {new Date(teamInfo?.trial_end_date).toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </p>
            </div>
            <p className="text-sm text-gray-500">
              Redirection vers ton espace équipe...
            </p>
          </>
        ) : error ? (
          // Erreur
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-[#1E3A5F] mb-2">
              Oups !
            </h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-[#1E3A5F] text-white rounded-lg hover:bg-[#152d4a] transition-colors"
            >
              Retour à l'accueil
            </button>
          </>
        ) : (
          // En attente / Confirmation
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#1E3A5F]/10 flex items-center justify-center">
              <Users className="w-10 h-10 text-[#1E3A5F]" />
            </div>
            <h1 className="text-2xl font-bold text-[#1E3A5F] mb-2">
              Rejoindre une équipe
            </h1>
            <p className="text-gray-600 mb-6">
              Tu as été invité à rejoindre une équipe sur AbracadaWorld
            </p>

            {/* Avantages */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
              <h3 className="font-semibold text-[#1E3A5F] mb-3">Ce qui t'attend :</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <Check size={16} className="text-green-500" />
                  Accès au CRM partagé
                </li>
                <li className="flex items-center gap-2">
                  <Check size={16} className="text-green-500" />
                  Tâches et projets collaboratifs
                </li>
                <li className="flex items-center gap-2">
                  <Check size={16} className="text-green-500" />
                  Tchat d'équipe
                </li>
                <li className="flex items-center gap-2">
                  <Check size={16} className="text-green-500" />
                  Agenda partagé
                </li>
              </ul>
            </div>

            {/* Prix */}
            <div className="bg-[#E8650A]/10 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-center gap-2 text-[#E8650A] font-semibold mb-1">
                <Gift size={20} />
                1er mois GRATUIT
              </div>
              <p className="text-sm text-gray-600">
                Puis 4,99€/mois
              </p>
            </div>

            <button
              onClick={joinTeam}
              disabled={loading}
              className="w-full py-3 bg-[#E8650A] text-white rounded-lg hover:bg-[#d55a09] transition-colors font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  Chargement...
                </>
              ) : (
                <>
                  <Users size={20} />
                  {isLoggedIn ? 'Rejoindre l\'équipe' : 'Me connecter et rejoindre'}
                </>
              )}
            </button>

            {!isLoggedIn && (
              <p className="text-xs text-gray-500 mt-4">
                Tu n'as pas encore de compte ?{' '}
                <a 
                  href="/reseau/inscription" 
                  className="text-[#E8650A] hover:underline"
                  onClick={() => localStorage.setItem('pendingTeamInvite', inviteCode)}
                >
                  Inscris-toi gratuitement
                </a>
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
